import classnames from 'classnames';
import { connect } from 'react-redux';
import autobind from 'core-decorators/lib/autobind';
import ui from 'redux-ui';
import { FormattedMessage } from 'react-intl';

import { WixConnect } from 'shared/utils/wix-connect';
import memoizedPartial from 'shared/utils/memoized-partial';
import { getNewRelic } from 'shared/analytics/new-relic';

import BaseView from 'widget/views/_base';

import MobilePlayer from 'widget/components/players/mobile-player';
import OverlayMainVideoMobile from 'widget/components/player-overlay/mobile/main-video/main-video';
import PlayersListSlidable from 'widget/components/players-list-slidable/players-list-slidable';

import { canPlayFullOrPreview } from 'widget/ui-selectors/video-access';
import { openMobileOverlay } from 'shared/utils/open-overlay';
import { openVideoPage } from 'widget/redux/client/actions/mobile-overlay-openners/main-openners';
import { INITIATORS, makeInitiatorQueryString } from 'shared/utils/initiators';
import { playerSizeMobile, playerSizeMobileMainItemForNewUX } from 'widget/utils/videos-sizes';

import { getVideosGroupedByIds } from 'shared/selectors/videos';

import { getMainVideoId } from 'widget/ui-selectors/selected-video-id';
import { isVideoPlaying } from 'widget/selectors/video-playback-status';

import { selectVideo } from 'widget/redux/client/actions/select-video';
import {
  playVideo,
  pauseVideo,
  resetPlaybackStatus,
} from 'widget/redux/client/actions/change-playback-status';

import { getVideoSelectedEventParams } from 'shared/utils/bi/video-overlay-events-params';
import { MOBILE_WIDGET_DATA_KEY } from 'widget/data-components/lazy-channel-videos';

import { LinkButton } from 'shared/components/button/button';
import Icon from 'shared/components/icon/icon';
import ResizedImage from 'shared/components/resized-image/resized-image';
import Header from 'widget/components/header-mobile/header-mobile';
import VideosListMobile from 'widget/components/videos-list-mobile/videos-list-mobile';
import PaymentEvents from 'shared/components/payment-events/payment-events';
import ReloadStorage from 'widget/components/reload-storage/reload-storage';
import { storeForReload } from 'widget/utils/reload';
import FakePlayer from 'widget/components/players-list-slidable/fake-player';

import styles from './mobile.styl';

import translations from './translations';
import widgetTranslations from 'widget/translations';

import paymentsHelper from 'widget/utils/payments';
import { isNewMobileUXEnabled } from 'shared/selectors/experiments';


@connect((state, ownProps) => ({
  isVideoPlaying: isVideoPlaying(state),
  currentChannelId: state.currentChannelId,
  appSettings: state.appSettings,
  videoByIds: getVideosGroupedByIds(state),
  firstChannelVideoId: state.firstChannelVideoId,
  newMobileUXIsEnabled: isNewMobileUXEnabled(state),
  videoIds: state.lazyVideoLists.mobileWidget.videoIds,
  mainVideoId: getMainVideoId(state, ownProps, MOBILE_WIDGET_DATA_KEY),
}), {
  openVideoPage,
  selectVideo,
  playVideo,
  pauseVideo,
  resetPlaybackStatus,
})
@ui()
@WixConnect(styles)
export default class MobileView extends BaseView {

  static displayName = 'MobileView';

  static propTypes = {
    resetPlaybackStatus: React.PropTypes.func,
    lazyChannelVideos: React.PropTypes.object,
    newMobileUXIsEnabled: React.PropTypes.bool,
    openVideoPage: React.PropTypes.func,
  };

  constructor(props) {
    super(props);

    getNewRelic().setCustomAttribute('layout', 'mobile');
  }

  get mainVideoList() {
    return this.props.lazyChannelVideos;
  }

  get playerSize() {
    const { newMobileUXIsEnabled, appSettings } = this.props;
    const widgetWidth = appSettings.requestedWidgetSize.width;

    return newMobileUXIsEnabled ?
      playerSizeMobileMainItemForNewUX(widgetWidth) :
      playerSizeMobile(widgetWidth);
  }

  get isFetching() {
    const { mainVideoList } = this;
    const { channel } = this.props;
    return channel.isFetching || mainVideoList.isFetching && !_.get(mainVideoList, 'ids.length');
  }

  @autobind
  processSubscriptionCancel() {
    const { appSettings: { siteUrl, instance }, currentChannelId } = this.props;

    paymentsHelper.cancelSubscription(currentChannelId, siteUrl, instance);
  }

  @autobind
  openPaymentPage(path) {
    path += `?${makeInitiatorQueryString(INITIATORS.WIDGET)}`;
    openMobileOverlay(path);
  }

  @autobind
  openMemberOnlyPage(id) {
    this.openPaymentPage(`#/membership/${id}`);
  }

  @autobind
  handlePlayStart() {
    this.disableChannelCover();
  }

  @autobind
  processDataFromStorage({ selectedVideoId }) {
    if (selectedVideoId) {
      this.selectVideo({ id: selectedVideoId });
    }
  }

  @autobind
  storeDataForReload() {
    storeForReload({
      selectedVideoId: this.props.mainVideoId,
    });
  }

  get player() {
    const videoItem = this.mainVideo;
    const playerOverlay = this.playerOverlay;
    const { width, height } = this.playerSize;

    const { appSettings, channelData } = this.props;

    if (videoItem) {
      return (
        <MobilePlayer styleName="player"
                      videoItem={videoItem}
                      channel={channelData}
                      width={width}
                      height={height}
                      canShowChannelCover={this.canShowChannelCover}
                      onPurchaseClick={this.openPurchase}
                      onSubscribeClick={this.openSubscription}
                      onPlayStart={this.handlePlayStart}
                      onMemberSignUp={memoizedPartial(this.openMemberOnlyPage, videoItem.id)}>
          {playerOverlay}
        </MobilePlayer>
      );
    }

    const coverUrl = channelData.customCoverUrl || channelData.coverUrl;

    if (!coverUrl) {
      return (
        <div styleName="no-player"
             style={{ width, height }}>
          <Icon styleName="no-player-play"
                name="arrow-right-filled" />
        </div>
      );
    }

    return (
      <ResizedImage styleName="overlay-only"
                    src={coverUrl}
                    width={width}
                    height={height}
                    params={{ lg: 1 }}>
        {
          React.cloneElement(playerOverlay, {
            showChannelCover: true,
            channelData,
            appSettings,
          })
        }
      </ResizedImage>
    );
  }

  @autobind
  playSelectedVideo() {
    this.disableChannelCover();
    const { channelData, currentSiteUser, selectVideo, playVideo } = this.props;

    const videoItem = this.mainVideo;
    const isPlayAllowed = canPlayFullOrPreview(channelData, videoItem, currentSiteUser.data);

    if (isPlayAllowed) {
      playVideo(videoItem.id);
    } else {
      selectVideo(videoItem.id);
    }
  }

  @autobind
  onCurrentSlideChanged(newIndex) {
    const { videoIds } = this.props;
    this.selectVideo({ id: videoIds[newIndex] });
  }

  get playerOverlay() {
    const videoItem = this.mainVideo || {};
    const openVideoPurchasePage = memoizedPartial(this.openPaymentPage, `#/video/purchase/${videoItem.id}`);
    const openSubscriptionPage = memoizedPartial(this.openPaymentPage, '#/channel/subscribe');

    return (
      <OverlayMainVideoMobile onPlayClick={this.playSelectedVideo}
                              onPurchaseClick={openVideoPurchasePage}
                              onSubscribeClick={openSubscriptionPage}
                              onTitleClick={openMobileOverlay} />
    );
  }

  clearStoredDataForMobileOverlay() {
    storeForReload({
      selectedVideoId: null,
    });
  }

  @autobind
  handleExploreChannelClick() {
    this.sendExploreThisChannelEvent();
    this.clearStoredDataForMobileOverlay();
    openMobileOverlay();
  }

  get footer() {
    if (this.props.channelData.videosCount === 0) {
      return (
        <div styleName="footer coming-soon"
             key="footer">
          <FormattedMessage {...widgetTranslations.thisChannelIsComingSoon} />
        </div>
      );
    }

    return (
      <div styleName="footer"
           key="footer">
        <LinkButton styleName="explore-channel"
                    onClick={this.handleExploreChannelClick}>
          <FormattedMessage {...translations.exploreThisChannel} />
          <Icon styleName="search-icon"
                name="permit-search" />
        </LinkButton>
      </div>
    );
  }

  sendExploreThisChannelEvent() {
    this.props.biEvent('widget.exploreThisChannel.clicked');
  }

  @autobind
  onVideoItemClicked(videoItemId) {
    this.disableChannelCover();
    this.sendVidSelectedBiEvent(videoItemId);
    this.props.resetPlaybackStatus();
  }

  get carousel() {
    const { mainVideoId, currentChannelId } = this.props;
    const { loadMore } = this.mainVideoList;

    if (this.isFetching || !this.mainVideo) {
      return null;
    }

    return (
      <VideosListMobile key="carousel"
                        styleName="videos-carousel"
                        currentChannelId={currentChannelId}
                        selectedVideoId={mainVideoId}
                        onVideoItemClicked={this.onVideoItemClicked}
                        loadMore={loadMore} />
    );
  }

  sendVidSelectedBiEvent(newVideoId) {
    const { appSettings, channelData, videoByIds } = this.props;
    this.props.biEvent(
      'widget.VidSelected.displayed',
      getVideoSelectedEventParams(appSettings, channelData, videoByIds[newVideoId], this.canShowChannelCover),
    );
  }

  get newMobileUI() {
    const { width, height } = this.playerSize;
    const { loadMore } = this.mainVideoList;
    const { videoIds, openVideoPage, isVideoPlaying } = this.props;
    const videoItem = this.mainVideo;

    if (_.size(videoIds) === 1) {
      return (
        <FakePlayer videoItem={videoItem}
                    useChannelCoverIfPossible={this.canShowChannelCover}
                    paused={!isVideoPlaying}
                    itemWidth={width}
                    itemHeight={height}
                    onPlayClick={memoizedPartial(openVideoPage, videoItem.id)}
                    onPlayStart={this.handlePlayStart}
                    onMemberSignUp={memoizedPartial(this.openMemberOnlyPage, videoItem.id)} />
      );
    }

    return (
      <PlayersListSlidable loadMore={loadMore}
                           paused={!isVideoPlaying}
                           itemWidth={width}
                           itemHeight={height}
                           canShowChannelCover={this.canShowChannelCover}
                           onCurrentSlideChanged={this.onCurrentSlideChanged}
                           onPlayClick={memoizedPartial(openVideoPage, videoItem.id)}
                           onPlayStart={this.handlePlayStart}
                           onMemberSignUp={memoizedPartial(this.openMemberOnlyPage, videoItem.id)}
                           selectedVideoId={videoItem && videoItem.id} />
    );
  }

  get mainUI() {
    return [
      <Header styleName="header"
              key="header"
              channel={this.props.channelData}
              onUserLogin={this.storeDataForReload}
              onUserLogout={this.storeDataForReload}
              onSubscribeClick={memoizedPartial(this.openPaymentPage, '#/channel/subscribe')}
              onSubscriptionCancelClick={this.processSubscriptionCancel} />,
      <div styleName="player-wrapper"
           key="wrapper">
        {this.player}
      </div>,
      this.carousel,
      this.footer,
    ];
  }

  get isVideosLoaded() {
    const { videoIds, lazyChannelVideos } = this.props;
    return !lazyChannelVideos.isFetching || !_.isEmpty(videoIds);
  }

  render() {
    if (!this.isUIBlockingDataLoaded || !this.isVideosLoaded) {
      return this.spinner;
    }

    const { appSettings, newMobileUXIsEnabled } = this.props;

    const styleNames = classnames('container', `view-mode-${appSettings.editMode}`);

    return (
      <section styleName={styleNames}>
        <ReloadStorage onReload={this.processDataFromStorage} />
        <PaymentEvents onSubscription={this.reset}
                       onSubscriptionCancel={this.reset} />

        { newMobileUXIsEnabled ? this.newMobileUI : this.mainUI }
      </section>
    );
  }
}



// WEBPACK FOOTER //
// ./src/widget/views/mobile/mobile.js